import {useCallback, useMemo, useState} from 'react'

export type SignInStepId = 'session' | 'account' | 'permissions' | 'dashboard'
export type SignInStepStatus = 'pending' | 'active' | 'done'

export interface SignInStep {
  id: SignInStepId
  label: string
  status: SignInStepStatus
}

export const SIGN_IN_STEP_LABELS: Record<SignInStepId, string> = {
  session: 'Confirming your Single Sign session',
  account: 'Loading your Staffology account',
  permissions: 'Applying your role and permissions',
  dashboard: 'Opening your dashboard',
}

const ORDER: SignInStepId[] = ['session', 'account', 'permissions', 'dashboard']

export function useSignInSteps({withSession}: {withSession: boolean}) {
  const ids = useMemo(() => (withSession ? ORDER : ORDER.slice(1)), [withSession])
  const [status, setStatus] = useState<Partial<Record<SignInStepId, SignInStepStatus>>>({})

  const start = useCallback(
    (id: SignInStepId) => setStatus((current) => ({...current, [id]: 'active'})),
    [],
  )
  const complete = useCallback(
    (id: SignInStepId) => setStatus((current) => ({...current, [id]: 'done'})),
    [],
  )
  const reset = useCallback(() => setStatus({}), [])

  const steps: SignInStep[] = useMemo(
    () => ids.map((id) => ({id, label: SIGN_IN_STEP_LABELS[id], status: status[id] ?? 'pending'})),
    [ids, status],
  )

  return useMemo(() => ({steps, start, complete, reset}), [steps, start, complete, reset])
}
